import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { PaperPlaneRight, Sparkle, User } from '@phosphor-icons/react';

export default function AIChatPage() {
  const { api, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await api.get('/ai/chat-history?limit=30');
        const history = res.data.map(c => ([
          { role: 'user', text: c.user_message, time: c.created_at },
          { role: 'ai', text: c.ai_response, time: c.created_at }
        ])).flat();
        setMessages(history);
      } catch (err) { console.error(err); }
    };
    fetchHistory();
  }, [api]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg, time: new Date().toISOString() }]);
    setLoading(true);
    try {
      const res = await api.post('/ai/chat', { message: msg });
      setMessages(prev => [...prev, { role: 'ai', text: res.data.response, time: new Date().toISOString() }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'ai', text: 'Sorry, I encountered an error. Please check your OpenAI API key in Settings.', time: new Date().toISOString() }]);
    }
    setLoading(false);
  };

  return (
    <div data-testid="ai-chat-page" className="flex flex-col h-[calc(100vh-8rem)] md:h-[calc(100vh-4rem)]">
      <div className="mb-4">
        <p className="overline">AI Assistant</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">HomeCommand AI</h1>
        <p className="text-sm text-muted-foreground mt-1">Ask me anything about your household</p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 pb-4">
        {messages.length === 0 && (
          <div className="text-center py-16">
            <Sparkle size={48} weight="duotone" className="mx-auto text-[#FF6B35] mb-4" />
            <p className="font-heading text-xl font-bold mb-2 ai-gradient-text">How can I help?</p>
            <p className="text-sm text-neutral-500 max-w-md mx-auto">
              Ask me about expenses, staff, schedule, or give me commands like "Pay cook salary" or "What did I spend on groceries this month?"
            </p>
            <div className="flex flex-wrap gap-2 justify-center mt-6">
              {['Show today\'s schedule', 'How much did I spend this month?', 'Suggest ways to save money', 'Generate morning briefing'].map(s => (
                <button key={s} onClick={() => { setInput(s); }} className="px-3 py-1.5 rounded-full border border-white/10 text-xs text-neutral-400 hover:bg-white/5 hover:text-white transition-all duration-200">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {m.role === 'ai' && (
              <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                <Sparkle size={16} weight="fill" className="text-accent" />
              </div>
            )}
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed
              ${m.role === 'user'
                ? 'bg-[#FF6B35] text-white btn-glow rounded-br-md'
                : 'bg-muted rounded-bl-md'}`}>
              <p className="whitespace-pre-wrap">{m.text}</p>
            </div>
            {m.role === 'user' && (
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <User size={16} className="text-primary" />
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
              <Sparkle size={16} weight="fill" className="text-accent animate-pulse" />
            </div>
            <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex gap-1.5">
                <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="flex gap-3 pt-4 border-t border-border">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          data-testid="ai-chat-input"
          placeholder="Ask anything about your household..."
          className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30 focus:bg-background transition-all"
          disabled={loading}
        />
        <button type="submit" data-testid="ai-chat-send-btn" disabled={loading || !input.trim()}
          className="px-5 py-3 rounded-xl bg-[#FF6B35] text-white btn-glow hover:opacity-90 transition-opacity disabled:opacity-50">
          <PaperPlaneRight size={20} weight="fill" />
        </button>
      </form>
    </div>
  );
}
